import React, {Fragment} from 'react';
import PropTypes from 'prop-types';
import cl from 'classnames';

import ColorMatchedComplements from '~/components/ColorMatchedComplements';
import Design from '~/components/Design';
import {Button, Typography} from '@customink/pigment-react';

import {iconPropType} from '~/data/records/icons';
import {isDark as isDarkColor} from '~/utils/colors';
import {metrics} from '~/vendor/metrics';
import withRouter, {routerPropTypes} from '~/router/withRouter';

import './index.scss';
import {scrollToTop} from '~/utils/scrolling';

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
function Complements(props) {
  const handleComplementClick = (styleId, colorId) => {
    props.onPickProduct(styleId, colorId);
    props.meta.addItemsPickStyle(styleId);
  };

  const handleBrowseCatalogClick = () => {
    scrollToTop(document.body, () => {
      props.meta.addItemsBrowseMore({
        source: 'complements'
      });
      metrics.fireEvent('complementary styles', 'quote', 'Browse More');
      props.router.navigate(
        `/productCatalogOrderMore/${anchorStyle.id}?quoteFormPath=true&addItemsPath=true`
      );
    });
  };

  const renderMultiItemComplementsFooter = () => {
    const designAreaLeft = {
      height: 95,
      width: 32,
      marginTop: 3,
      position: 'relative'
    };
    const designAreaMiddle = {
      height: 100,
      width: 33,
      position: 'relative'
    };
    const designAreaRight = {
      height: 97,
      width: 30,
      marginTop: 3,
      position: 'relative'
    };

    const footerImgClassname = cl('ndx-Complements-multiItem-footer-img', {
      isDark: isDarkColor(props.selectedProductColor)
    });
    return (
      <div className="ndx-Complements-multiItem-footer ndx-Complements-multiItem-footer--wide">
        <div className="ndx-Complements-multiItem-footer-divider" />
        <div className="ndx-Complements-multiItem-footer-imgContainer">
          <div className={footerImgClassname}>
            <Design
              blankDesign={false}
              icons={props.icons}
              interactive={false}
              productRendered
              multiItemOverlay
              scale={3.1}
              selectedIconId={null}
              selectedViewId={1}
              showGuidelines={false}
              zoomConstraint={designAreaLeft}
              productColor={props.selectedProductColor}
            />
            <Design
              blankDesign={false}
              icons={props.icons}
              interactive={false}
              productRendered
              multiItemOverlay
              scale={3.1}
              selectedIconId={null}
              selectedViewId={1}
              showGuidelines={false}
              zoomConstraint={designAreaMiddle}
              productColor={props.selectedProductColor}
            />
            <Design
              blankDesign={false}
              icons={props.icons}
              interactive={false}
              productRendered
              multiItemOverlay
              scale={3.1}
              selectedIconId={null}
              selectedViewId={1}
              showGuidelines={false}
              zoomConstraint={designAreaRight}
              productColor={props.selectedProductColor}
            />
          </div>
        </div>
        <Typography
          component="div"
          variant="paragraphSmall"
          className="ndx-Complements-multiItem-footer-label">
          We've got even more styles to suit your group and your budget.
        </Typography>
        <Button
          size="large"
          className="cta-browse-more"
          variant="secondary"
          onClick={handleBrowseCatalogClick}>
          Browse more styles
        </Button>
      </div>
    );
  };

  const renderHeader = () => {
    if (props.header && !props.isMultiItemEligible) {
      return (
        <div className="ndx-Complements-header">
          <Typography variant="heading5">{props.header}</Typography>
        </div>
      );
    } else if (props.isMultiItemEligible) {
      return (
        <div className="ndx-Complements-header">
          <Typography variant="heading5">Buy more, save more</Typography>
          <Typography variant="paragraphSmall" sx={{textAlign: 'inherit'}}>
            Add your design to more styles and colors that work for everyone and
            boost your volume discount.
          </Typography>
        </div>
      );
    } else {
      return (
        <Fragment>
          <Typography
            component="p"
            variant="heading5"
            className="ndx-Complements-header ndx-Complements-header--extraMargin"
            data-testid="complements-header">
            Add your design to more great products.
          </Typography>
          {props.orderMoreEnabled && (
            <Typography
              component="p"
              variant="paragraphSmall"
              className="ndx-Complements-subHeader"
              data-testid="complements-subheader">
              These matching styles will go great with your design
            </Typography>
          )}
        </Fragment>
      );
    }
  };

  const {
    anchorStyle,
    complements,
    complementsLoaded,
    selectedProduct,
    selectedProducts,
    selectedProductType,
    showAnchorStyle,
    hasCompatibleStyles,
    hasSleeveDesign,
    inSkirt,
    isFundraisingMode,
    isMultiItemEligible,
    isWide,
    limitComplementCount,
    orderMoreEnabled,
    quoteFormPath,
    meta,
    onClickOrderMore
  } = props;

  const showOrderMore = props.showOrderMore
    ? hasCompatibleStyles && !isFundraisingMode && !hasSleeveDesign
    : props.showOrderMore;

  const complementsClassName = cl('ndx-Complements', {
    isMultiItem: props.isMultiItemEligible
  });

  return (
    <div className={complementsClassName}>
      <div className="ndx-Complements-header" data-testid="complements-header">
        {renderHeader()}
      </div>
      {complementsLoaded && (
        <ColorMatchedComplements
          addStylesMetrics={onClickOrderMore}
          anchorStyle={anchorStyle}
          complementStyles={complements}
          fireSkirtTag={meta.addComplement}
          hasCompatibleStyles={hasCompatibleStyles}
          hasSleeveDesign={hasSleeveDesign}
          inSkirt={inSkirt}
          isMultiItemEligible={isMultiItemEligible}
          isWide={isWide}
          limitComplementCount={limitComplementCount}
          orderMoreEnabled={orderMoreEnabled}
          quoteFormPath={quoteFormPath}
          selectedProduct={selectedProduct}
          selectedProducts={selectedProducts}
          selectedProductType={selectedProductType}
          showAnchorStyle={showAnchorStyle || isMultiItemEligible}
          showOrderMore={showOrderMore}
          onComplementClick={handleComplementClick}
        />
      )}
      {orderMoreEnabled && (
        <div
          className="ndx-Complements-subHeader"
          data-testid="complements-subheader">
          These matching styles will go great with your design
        </div>
      )}
      {props.isMultiItemEligible &&
        hasCompatibleStyles &&
        renderMultiItemComplementsFooter()}
    </div>
  );
}

Complements.displayName = 'Complements';

const propTypes = {
  anchorStyle: PropTypes.object,
  complements: PropTypes.arrayOf(
    PropTypes.shape({
      complementId: PropTypes.string,
      style: PropTypes.object
    })
  ),
  complementsLoaded: PropTypes.bool,
  hasCompatibleStyles: PropTypes.bool,
  hasSleeveDesign: PropTypes.bool,
  header: PropTypes.string,
  icons: PropTypes.arrayOf(iconPropType),
  inSkirt: PropTypes.bool,
  isFundraisingMode: PropTypes.bool,
  isMultiItemEligible: PropTypes.bool,
  isWide: PropTypes.bool,
  limitComplementCount: PropTypes.bool,
  meta: PropTypes.objectOf(PropTypes.func),
  orderMoreEnabled: PropTypes.bool,
  quoteFormPath: PropTypes.bool,
  router: routerPropTypes,
  selectedProduct: PropTypes.object,
  selectedProductColor: PropTypes.object,
  selectedProducts: PropTypes.array,
  selectedProductType: PropTypes.string,
  showAnchorStyle: PropTypes.bool,
  showOrderMore: PropTypes.bool,
  onClickOrderMore: PropTypes.func,
  onPickProduct: PropTypes.func
};

Complements.propTypes = propTypes;

Complements.defaultProps = {
  isFundraisingMode: false,
  orderMoreEnabled: false,
  hasCompatibleStyles: false,
  limitComplementCount: false,
  quoteFormPath: false,
  onClickOrderMore: () => {},
  meta: {
    orderMoreAddStyles() {}
  }
};

export default withRouter(Complements);
